import { Injectable } from '@angular/core';
import {
  CanActivate,
  CanLoad,
  Route,
  UrlSegment,
  Router,
  ActivatedRouteSnapshot
} from '@angular/router';
import { UserService } from '../services/user.service';
import { environment } from 'src/environments/environment';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, take, tap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RoleGuard implements CanActivate, CanLoad {

  private rolesLoad$: Observable<string[]> | null = null;

  constructor(
    private userService: UserService,
    private router: Router
  ) {}

  // Determina los roles permitidos según la sección (URL)
  private getAllowedRoles(path: string): string[] {
    if (path.startsWith('curriculum')) return ['ASESOR_CV', 'ADMIN'];
    if (path.startsWith('job-offers')) return ['GESTOR_OFERTAS', 'ADMIN'];
    if (path.startsWith('management')) return ['USER_ADMIN', 'ADMIN'];
    return []; // rutas abiertas (accesibilidad, mapa-web, etc.)
  }

  private loadRolesOnce(): Observable<string[]> {
    const existing = this.userService.getUserRoles() || [];
    if (existing.length > 0) return of(existing);

    if (this.rolesLoad$) return this.rolesLoad$;

    const document = this.userService.getUserDocument() || '';
    this.rolesLoad$ = this.userService.getUserByDocument(document).pipe(
      map(user => user?.roles ?? []),
      tap(roles => {
        if (roles.length > 0) this.userService.setUserRoles(roles);
      }),
      catchError(error => {
        return of([]);
      }),
      take(1),
      finalize(() => {
        this.rolesLoad$ = null;
      })
    );

    return this.rolesLoad$;
  }

  // Verifica si el usuario tiene acceso
  private hasAccess(
    allowedRoles: string[],
    context: { kind: 'canActivate' | 'canLoad'; path: string }
  ): Observable<boolean> {
    if (!environment.securityEnabled) return of(true);

    // Si no hay roles requeridos → ruta abierta
    if (allowedRoles.length === 0) return of(true);

    return this.loadRolesOnce().pipe(
      map(userRoles => {
        const access = userRoles.some(r => allowedRoles.includes(r));
        if (!access) {
          this.router.navigate(['/unauthorized'], {
            state: {
              source: 'RoleGuard',
              reason: userRoles.length === 0 ? 'roles_empty' : 'access_denied',
              context,
              allowedRoles,
              userRoles,
            },
          });
        }
        return access;
      }),
      take(1)
    );
  }

  // Para navegación interna
  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    const path = route.url.map(seg => seg.path).join('/');
    const allowedRoles = this.getAllowedRoles(path);
    return this.hasAccess(allowedRoles, { kind: 'canActivate', path });
  }

  // Para carga de módulos lazy-loaded
  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean> {
    const path = route.path || '';
    const allowedRoles = this.getAllowedRoles(path);
    return this.hasAccess(allowedRoles, { kind: 'canLoad', path });
  }
}
